import type { TFunction } from 'i18next'
import { describeRecurrence } from '@/src/renderer/lib/recurrence/describeRecurrence'
import type { BackupProfile, BackupTables } from './format'
import { exportDate, exportDateTime, htmlToPlainText, plainText, type Translate } from './exportText'

/**
 * **Export als CSV** (#252): je Art eine Datei, zusammen in einer ZIP-Datei. Lesbare Spalten (Projekt,
 * Abschnitt, Tags beim Namen) und dazu die Kennungen, damit sich Bezüge in einer Tabelle herstellen
 * lassen. Erledigtes gehört dazu, mit Spalte „Status“ (Entscheidung 18) — auch erledigte Runden
 * wiederkehrender Aufgaben, die als eigene Zeilen liegen.
 *
 * Excel erwartet das Listentrennzeichen des Systems: Deutsch `;`, sonst `,`. UTF-8 mit BOM, damit
 * Umlaute ankommen.
 */

type Cell = string | number | boolean | null | undefined

export interface CsvExportInput {
  tables: BackupTables
  /** Nur als Auskunft (Entscheidung 7); `null`, wenn „Profil“ nicht gewählt ist. */
  profile: BackupProfile | null
  /** Die App-Sprache — bestimmt Trennzeichen und Spaltennamen. */
  language: string
  t: Translate
}

export const csvDelimiter = (language: string): ';' | ',' => (language.toLowerCase().startsWith('de') ? ';' : ',')

/**
 * Eine Zelle. Beginnt sie wie eine Formel (`=`, `+`, `-`, `@`, Tab, Zeilenrücklauf), bekommt sie ein `'`
 * davor — sonst führte eine Tabellen-App einen Aufgabentitel als Formel aus.
 */
export function csvCell(value: Cell, delimiter: string): string {
  if (value === null || value === undefined) return ''
  let text = String(value)
  if (/^[=+\-@\t\r]/.test(text)) text = `'${text}`
  return /["\r\n]/.test(text) || text.includes(delimiter) || text !== text.trim() ? `"${text.replace(/"/g, '""')}"` : text
}

/** Eine ganze Datei: BOM, Kopfzeile, Zeilen mit CRLF. */
export function csvText(header: readonly string[], rows: readonly Cell[][], delimiter: string): string {
  const line = (cells: readonly Cell[]) => cells.map(cell => csvCell(cell, delimiter)).join(delimiter)
  return `\uFEFF${[line(header), ...rows.map(line)].join('\r\n')}\r\n`
}

/** Die Dateien des Exports, Name → Inhalt. Leere Arten fehlen, Aufgaben und Projekte stehen immer da. */
export function buildCsvFiles({ tables, profile, language, t }: CsvExportInput): Record<string, string> {
  const delimiter = csvDelimiter(language)
  const col = (key: string) => t(`preferences.export.file.column.${key}`)
  const fileName = (key: string) => `${t(`preferences.export.file.name.${key}`)}.csv`
  const lifeSpace = (value: string) => t(`lifeSpaces.${value}`)
  const files: Record<string, string> = {}
  const add = (key: string, columns: string[], rows: Cell[][], always = false) => {
    if (rows.length > 0 || always) files[fileName(key)] = csvText(columns.map(col), rows, delimiter)
  }

  const projectName = new Map(tables.projects.map(p => [p.id, p.name]))
  const sectionTitle = new Map(tables.sections.map(s => [s.id, s.title]))
  const taskTitle = new Map(tables.tasks.map(task => [task.id, task.title]))
  const tagName = new Map(tables.tags.map(tag => [tag.id, tag.name]))
  const habitTitle = new Map(tables.habits.map(h => [h.id, h.title]))
  const tagsOf = (ids: readonly string[]) => ids.map(id => tagName.get(id)).filter(Boolean).join(', ')
  const recurrence = (rule: Parameters<typeof describeRecurrence>[0] | null) => {
    if (!rule) return ''
    try {
      return describeRecurrence(rule, t as unknown as TFunction, language)
    } catch {
      return ''
    }
  }

  add('tasks', [
    'title', 'status', 'completedAt', 'lifeSpace', 'project', 'section', 'parentTask', 'startDate', 'startTime',
    'dueDate', 'dueTime', 'tags', 'recurrence', 'description', 'createdAt', 'id', 'projectId', 'sectionId', 'parentTaskId',
  ], tables.tasks.map(task => [
      task.title,
      t(`preferences.export.file.value.${task.status === 'completed' || task.completedAt ? 'completed' : 'open'}`),
      exportDateTime(task.completedAt),
      lifeSpace(task.lifeSpace),
      task.projectId ? projectName.get(task.projectId) : '',
      task.sectionId ? sectionTitle.get(task.sectionId) : '',
      task.parentTaskId ? taskTitle.get(task.parentTaskId) : '',
      exportDate(task.startDate),
      task.startTime,
      exportDate(task.dueDate),
      task.dueTime,
      tagsOf(task.tags),
      recurrence(task.recurrence),
      plainText(task.description),
      exportDateTime(task.createdAt),
      task.id,
      task.projectId,
      task.sectionId,
      task.parentTaskId,
    ]), true)

  add('projects', ['name', 'lifeSpace', 'parentProject', 'status', 'description', 'createdAt', 'id', 'parentProjectId'],
    [...tables.projects].sort((a, b) => a.order - b.order).map(p => [
      p.name,
      lifeSpace(p.lifeSpace),
      p.parentId ? projectName.get(p.parentId) : '',
      t(`preferences.export.file.value.project_${p.status}`),
      plainText(p.description),
      exportDateTime(p.createdAt),
      p.id,
      p.parentId,
    ]), true)

  add('sections', ['title', 'project', 'id', 'projectId'],
    tables.sections.map(s => [s.title, projectName.get(s.projectId), s.id, s.projectId]))

  add('tags', ['name', 'color', 'id'], tables.tags.map(tag => [tag.name, tag.color, tag.id]))

  add('goals', ['title', 'lifeSpace', 'why', 'id'],
    tables.goals.map(g => [g.title, lifeSpace(g.lifeSpace), plainText(g.description), g.id]))

  add('habits', ['title', 'lifeSpace', 'startDate', 'pausedSince', 'notes', 'id'],
    tables.habits.map(h => [h.title, lifeSpace(h.lifeSpace), exportDate(h.startDate), exportDate(h.pausedAt), plainText(h.notes), h.id]))

  add('habitLogs', ['habit', 'date', 'done', 'habitId'],
    [...tables.habitLogs].sort((a, b) => a.date.localeCompare(b.date)).map(log => [
      habitTitle.get(log.habitId),
      log.date,
      t(`preferences.export.file.value.${log.completed ? 'yes' : 'no'}`),
      log.habitId,
    ]))

  add('comments', ['refersTo', 'title', 'text', 'postedAt', 'id'], [
    ...tables.taskComments.map(c => [t('preferences.export.file.value.task'), taskTitle.get(c.taskId), plainText(c.content), exportDateTime(c.postedAt), c.id]),
    ...tables.projectComments.map(c => [t('preferences.export.file.value.project'), projectName.get(c.projectId), plainText(c.content), exportDateTime(c.postedAt), c.id]),
  ])

  add('canvas', ['project', 'title', 'text', 'id'],
    tables.canvasNotes.map(n => [projectName.get(n.projectId), n.title, n.kind === 'text' ? htmlToPlainText(n.content) : plainText(n.content), n.id]))

  add('focusTimes', ['task', 'start', 'end', 'minutes', 'taskId'],
    tables.timeEntries.map(e => [
      taskTitle.get(e.taskId),
      exportDateTime(e.startTime),
      exportDateTime(e.endTime),
      e.endTime ? Math.round((e.endTime.getTime() - e.startTime.getTime()) / 60_000) : '',
      e.taskId,
    ]))

  add('reminders', ['task', 'remindDate', 'remindTime', 'offsetMinutes', 'taskId'],
    tables.taskReminders.map(r => [taskTitle.get(r.taskId), r.remindDate, r.remindTime, r.offsetMinutes, r.taskId]))

  if (profile) {
    add('profile', ['field', 'value'], [
      [col('fullName'), profile.fullName],
      [col('displayName'), profile.displayName],
      [col('email'), profile.email],
    ])
  }

  add('settings', ['key', 'value'], [
    ...tables.settings.map(s => [s.key, typeof s.value === 'string' ? s.value : JSON.stringify(s.value)]),
    ...tables.userPreferences.map(p => [p.key, typeof p.value === 'string' ? p.value : JSON.stringify(p.value)]),
  ])

  return files
}
